/**
 * API Client for AI Website Builder
 * Handles all requests to the backend API
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

// Token getter function - set by App component
let getSessionToken: (() => Promise<string | null>) | null = null;

export function setTokenGetter(getter: () => Promise<string | null>) {
  getSessionToken = getter;
}

export async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const controller = new AbortController();
  const timeout = 120000; // 2 minutes
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Get Clerk session token if available
  if (getSessionToken) {
    try {
      const token = await getSessionToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to get session token:', error);
    }
  } else {
    // Fallback for development without Clerk
    const customerId = localStorage.getItem('customerId');
    if (customerId) {
      headers['X-Customer-ID'] = customerId;
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new APIError(response.status, error.error || 'Request failed');
    }

    return response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new APIError(408, 'The request took too long to complete. Please try again later.');
    }
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(500, error.message || 'An unknown error occurred');
  }
}

// Wizard API
export const wizard = {
  start: async () => {
    return fetchAPI<{ success: boolean; wizardId: string; state: any }>('/wizard/start', {
      method: 'POST',
    });
  },

  getState: async (wizardId: string) => {
    return fetchAPI<{ success: boolean; state: any }>(`/wizard/${wizardId}`);
  },

  updateStep: async (wizardId: string, step: number, answers: any) => {
    return fetchAPI<{ success: boolean; state: any }>(`/wizard/${wizardId}/step`, {
      method: 'PUT',
      body: JSON.stringify({ step, answers }),
    });
  },

  submit: async (wizardId: string) => {
    return fetchAPI<{ success: boolean; wizardId: string; message: string }>(`/wizard/${wizardId}/submit`, {
      method: 'POST',
    });
  },

  chatMessage: async (wizardId: string, message: string) => {
    return fetchAPI<{
      success: boolean;
      response: string;
      extractedData: any;
      confidence: any;
      canGenerate: boolean;
      phase: string;
      conversation: any[];
    }>(`/wizard/${wizardId}/chat/message`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },
};

// Page Wizard API
export const pageWizard = {
  start: async (siteId: string) => {
    return fetchAPI<{ success: boolean; wizardId: string; state: any }>('/page-wizard/start', {
      method: 'POST',
      body: JSON.stringify({ siteId }),
    });
  },

  getState: async (wizardId: string) => {
    return fetchAPI<{ success: boolean; state: any }>(`/page-wizard/${wizardId}`);
  },

  chatMessage: async (wizardId: string, message: string) => {
    return fetchAPI<{
      success: boolean;
      response: string;
      extractedData: any;
      confidence: any;
      suggestedBlocks: string[];
      canGenerate: boolean;
      phase: string;
      conversation: any[];
    }>(`/page-wizard/${wizardId}/chat/message`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  delete: async (wizardId: string) => {
    return fetchAPI<{ success: boolean; message: string }>(`/page-wizard/${wizardId}`, {
      method: 'DELETE',
    });
  },

  generate: async (wizardId: string, suggestedBlocks: string[]) => {
    return fetchAPI<{
      success: boolean;
      containers: any[];  // ✅ Changed from blocks to containers
      seo: { title: string; description: string; keywords: string[] };
      pageData: { path: string; title: string };
    }>(`/page-wizard/${wizardId}/generate`, {
      method: 'POST',
      body: JSON.stringify({
        suggestedBlocks,
        useMCP: true,  // Always use MCP generator for proper Container/Primitive structure
        useToolCalling: false  // Deprecated old generator
      }),
    });
  },
};

// Generation API
export const generate = {
  start: async (wizardId: string) => {
    return fetchAPI<{ success: boolean; jobId: string; estimatedTime: string }>('/generate/start', {
      method: 'POST',
      body: JSON.stringify({ wizardId }),
    });
  },

  getStatus: async (jobId: string) => {
    return fetchAPI<{ success: boolean; job: any }>(`/generate/status/${jobId}`);
  },
};

// Pages API
export const pages = {
  list: async (siteId?: string) => {
    const url = siteId ? `/pages?siteId=${siteId}` : '/pages';
    return fetchAPI<{ success: boolean; pages: any[] }>(url);
  },

  get: async (pageId: string) => {
    return fetchAPI<{ success: boolean; page: any }>(`/pages/${pageId}`);
  },

  getByPath: async (path: string) => {
    return fetchAPI<{ success: boolean; page: any }>(`/pages/by-path${path}`);
  },

  create: async (data: { path: string; title: string; blocks?: any[]; containers?: any[]; seo?: any; siteId?: string }) => {
    return fetchAPI<{ success: boolean; page: any }>('/pages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (pageId: string, data: Partial<{ title: string; path: string; blocks: any[]; seo: any; published: boolean }>) => {
    return fetchAPI<{ success: boolean }>(`/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (pageId: string) => {
    return fetchAPI<{ success: boolean }>(`/pages/${pageId}`, {
      method: 'DELETE',
    });
  },

  duplicate: async (pageId: string) => {
    return fetchAPI<{ success: boolean; pageId: string; path: string }>(`/pages/${pageId}/duplicate`, {
      method: 'POST',
    });
  },

  updateSEO: async (pageId: string, seo: any) => {
    return fetchAPI<{ success: boolean; message: string }>(`/pages/${pageId}/seo`, {
      method: 'PATCH',
      body: JSON.stringify({ seo }),
    });
  },
};

// Chat API
export const chat = {
  edit: async (pageId: string, message: string, selection?: any, context?: any) => {
    return fetchAPI<{ success: boolean; response: any; pageUpdated: boolean }>('/chat/edit', {
      method: 'POST',
      body: JSON.stringify({ pageId, message, selection, context }),
    });
  },

  getHistory: async (pageId: string) => {
    return fetchAPI<{ success: boolean; conversations: any[] }>(`/chat/history/${pageId}`);
  },

  getSuggestions: async (pageId: string, type: 'content' | 'design' | 'seo' | 'all') => {
    return fetchAPI<{ success: boolean; suggestions: string[] }>('/chat/suggest', {
      method: 'POST',
      body: JSON.stringify({ pageId, type }),
    });
  },
};

// Revisions API
export const revisions = {
  list: async (siteId: string, limit = 50, offset = 0) => {
    return fetchAPI<{ success: boolean; revisions: any[]; total: number }>(`/revisions?siteId=${siteId}&limit=${limit}&offset=${offset}`);
  },

  get: async (revisionId: string) => {
    return fetchAPI<{ success: boolean; revision: any }>(`/revisions/${revisionId}`);
  },

  create: async (siteId: string, message: string, createdBy: string) => {
    return fetchAPI<{ success: boolean; revisionId: string }>('/revisions', {
      method: 'POST',
      body: JSON.stringify({ siteId, message, createdBy }),
    });
  },

  restore: async (revisionId: string) => {
    return fetchAPI<{ success: boolean; message: string; backupRevisionId: string }>(`/revisions/${revisionId}/restore`, {
      method: 'POST',
    });
  },

  delete: async (revisionId: string) => {
    return fetchAPI<{ success: boolean }>(`/revisions/${revisionId}`, {
      method: 'DELETE',
    });
  },

  compare: async (id1: string, id2: string) => {
    return fetchAPI<{ success: boolean; revision1: any; revision2: any; differences: any }>(`/revisions/compare/${id1}/${id2}`);
  },
};

// Customers API
export const customers = {
  create: async (data: { businessName: string; email: string; clerkUserId?: string; subdomain?: string }) => {
    return fetchAPI<{ success: boolean; customer: any }>('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getMe: async () => {
    // JWT token is sent automatically by fetchAPI via Authorization header
    // The API will extract clerkUserId from the token
    return fetchAPI<{ success: boolean; customer: any }>('/customers/me');
  },

  get: async (customerId: string) => {
    return fetchAPI<{ success: boolean; customer: any }>(`/customers/${customerId}`);
  },

  update: async (customerId: string, data: Partial<{ businessName: string; email: string; subdomain: string }>) => {
    return fetchAPI<{ success: boolean }>(`/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// Media API
export const media = {
  upload: async (file: File, siteId: string, alt?: string, type?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('siteId', siteId);
    if (alt) formData.append('alt', alt);
    if (type) formData.append('type', type);

    const customerId = localStorage.getItem('customerId');
    const headers: Record<string, string> = {};
    if (customerId) {
      headers['X-Customer-ID'] = customerId;
    }

    const response = await fetch(`${API_BASE_URL}/media/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new APIError(response.status, error.error || 'Upload failed');
    }

    return response.json() as Promise<{ success: boolean; media: any }>;
  },

  list: async (siteId?: string, limit = 50, offset = 0) => {
    const url = siteId ? `/media?siteId=${siteId}&limit=${limit}&offset=${offset}` : `/media?limit=${limit}&offset=${offset}`;
    return fetchAPI<{ success: boolean; media: any[]; total: number }>(url);
  },

  get: async (mediaId: string) => {
    return fetchAPI<{ success: boolean; media: any }>(`/media/${mediaId}`);
  },

  update: async (mediaId: string, data: { alt: string }) => {
    return fetchAPI<{ success: boolean }>(`/media/${mediaId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (mediaId: string) => {
    return fetchAPI<{ success: boolean }>(`/media/${mediaId}`, {
      method: 'DELETE',
    });
  },

  getUsage: async (mediaId: string) => {
    return fetchAPI<{
      success: boolean;
      usage: Array<{
        pageId: string;
        pagePath: string;
        pageTitle: string;
        blockId: string;
        blockType: string;
        location: string;
      }>;
      usageCount: number;
    }>(`/media/${mediaId}/usage`);
  },

  replace: async (mediaId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const customerId = localStorage.getItem('customerId');
    const headers: Record<string, string> = {};
    if (customerId) {
      headers['X-Customer-ID'] = customerId;
    }

    const response = await fetch(`${API_BASE_URL}/media/${mediaId}/replace`, {
      method: 'PUT',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Replace failed' }));
      throw new APIError(response.status, error.error || 'Replace failed');
    }

    return response.json() as Promise<{ success: boolean; media: any }>;
  },

  regenerate: async (mediaId: string, prompt: string) => {
    return fetchAPI<{ success: boolean; media: any }>(`/media/${mediaId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  },

  listVideos: async () => {
    return fetchAPI<{
      success: boolean;
      videos: Array<{
        id: string;
        videoUrl: string;
        provider: string;
        title?: string;
        thumbnail?: string;
        usage: Array<{
          pageId: string;
          pagePath: string;
          pageTitle: string;
          blockId: string;
        }>;
      }>;
      count: number;
    }>(`/media/videos/list`);
  },
};

// Contact API
export const contact = {
  submit: async (data: { name?: string; email: string; message: string; [key: string]: any }) => {
    return fetchAPI<{ success: boolean; message: string; submissionId: string; emailSent: boolean }>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getSubmissions: async (limit = 50, offset = 0) => {
    return fetchAPI<{ success: boolean; submissions: any[] }>(`/contact/submissions?limit=${limit}&offset=${offset}`);
  },
};

// Design System API
export const design = {
  get: async () => {
    return fetchAPI<{ success: boolean; designSystem: any }>('/design');
  },

  update: async (updates: {
    brand?: any;
    colors?: any;
    typography?: any;
    spacing?: any;
    borderRadius?: any;
    shadows?: any;
    components?: any;
    imagery?: any;
  }) => {
    return fetchAPI<{ success: boolean; message: string }>('/design', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  getPresets: async () => {
    return fetchAPI<{ success: boolean; presets: any[] }>('/design/presets');
  },

  applyPreset: async (presetId: string) => {
    return fetchAPI<{ success: boolean; message: string }>('/design/apply-preset', {
      method: 'POST',
      body: JSON.stringify({ presetId }),
    });
  },

  savePreset: async (presetData: {
    name: string;
    description: string;
    colors: any;
    typography: any;
    spacing: any;
    borderRadius: any;
    shadows: any;
    components: any;
  }) => {
    return fetchAPI<{ success: boolean; message: string; presetId: string }>('/design/presets', {
      method: 'POST',
      body: JSON.stringify(presetData),
    });
  },

  generateColors: async (prompt: string) => {
    return fetchAPI<{ success: boolean; colors: any }>('/design/generate-colors', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  },
};

// Sites API
export const sites = {
  list: async () => {
    return fetchAPI<{ success: boolean; sites: any[] }>('/sites');
  },

  get: async (siteId: string) => {
    return fetchAPI<{ success: boolean; site: any }>(`/sites/${siteId}`);
  },

  create: async (data: { name: string; subdomain: string }) => {
    return fetchAPI<{ success: boolean; site: any }>('/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (siteId: string, data: Partial<{ name: string; subdomain: string }>) => {
    return fetchAPI<{ success: boolean }>(`/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (siteId: string) => {
    return fetchAPI<{ success: boolean }>(`/sites/${siteId}`, {
      method: 'DELETE',
    });
  },

  setPrimary: async (siteId: string) => {
    return fetchAPI<{ success: boolean; message: string }>(`/sites/${siteId}/set-primary`, {
      method: 'POST',
    });
  },

  getCloneData: async (siteId: string) => {
    return fetchAPI<{
      success: boolean;
      cloneData: {
        siteName: string;
        industry: string | null;
        stylePreference: 'modern' | 'classic' | 'bold' | 'minimal';
        preferredColors: string[];
      };
    }>(`/sites/${siteId}/clone-data`);
  },

  enableMaintenance: async (siteId: string) => {
    return fetchAPI<{ success: boolean; message: string }>(`/sites/${siteId}/maintenance/enable`, {
      method: 'POST',
    });
  },

  disableMaintenance: async (siteId: string) => {
    return fetchAPI<{ success: boolean; message: string }>(`/sites/${siteId}/maintenance/disable`, {
      method: 'POST',
    });
  },

  getAnalytics: async (siteId: string) => {
    return fetchAPI<{ success: boolean; analytics: any }>(`/sites/${siteId}/analytics`);
  },

  updateAnalytics: async (siteId: string, analytics: any) => {
    return fetchAPI<{ success: boolean; message: string; analytics: any }>(`/sites/${siteId}/analytics`, {
      method: 'PATCH',
      body: JSON.stringify({ analytics }),
    });
  },
};

// Domains API
export const domains = {
  list: async (siteId: string) => {
    return fetchAPI<{ success: boolean; domains: any[] }>(`/domains?siteId=${siteId}`);
  },

  add: async (data: { siteId: string; domain: string }) => {
    return fetchAPI<{ success: boolean; domain: any }>('/domains', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  verify: async (domainId: string) => {
    return fetchAPI<{ success: boolean; domain: any; verified: boolean }>(`/domains/${domainId}/verify`, {
      method: 'POST',
    });
  },

  getVerificationInstructions: async (domainId: string) => {
    return fetchAPI<{ success: boolean; instructions: any }>(`/domains/${domainId}/verification-instructions`);
  },

  delete: async (domainId: string) => {
    return fetchAPI<{ success: boolean }>(`/domains/${domainId}`, {
      method: 'DELETE',
    });
  },

  setPrimary: async (domainId: string) => {
    return fetchAPI<{ success: boolean; message: string }>(`/domains/${domainId}/set-primary`, {
      method: 'POST',
    });
  },
};

// AI Assistant API (Global Chat)
export const aiAssistant = {
  chat: async (data: {
    message: string;
    context: { path: string; siteId: string | null; siteName: string | null };
    activeWizardId?: string | null;
  }) => {
    return fetchAPI<{
      success: boolean;
      response: string;
      actions?: Array<{
        label: string;
        action: string;
        path?: string;
        variant?: string;
      }>;
      metadata?: Record<string, any>;
      wizardId?: string;
      pageCreated?: boolean;
      pageId?: string;
    }>('/ai-assistant/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  cleanup: async (wizardId: string) => {
    return fetchAPI<{ success: boolean; message: string }>(`/ai-assistant/${wizardId}/cleanup`, {
      method: 'DELETE',
    });
  },
};

// Settings (stored in localStorage for now)
export const settings = {
  getCustomerId: () => localStorage.getItem('customerId'),
  setCustomerId: (id: string) => localStorage.setItem('customerId', id),
  getSubdomain: () => localStorage.getItem('subdomain'),
  setSubdomain: (subdomain: string) => localStorage.setItem('subdomain', subdomain),
  getSiteId: () => localStorage.getItem('siteId'),
  setSiteId: (id: string) => localStorage.setItem('siteId', id),
};

// Pattern Library API (replaces section templates)
// Patterns are pre-built container+primitive combinations

export interface Pattern {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  thumbnail?: string;
}

export const getPatterns = async (params?: {
  category?: string;
  tags?: string[];
  sortBy?: 'name' | 'category';
  limit?: number;
  offset?: number;
}) => {
  // Patterns are defined in @ai-website-builder/types/src/patterns.ts
  // They are static templates, not stored in the database

  const patterns: Pattern[] = [
    { id: 'pattern_centered_hero', name: 'Centered Hero', description: 'Centered hero with headline, subheadline, and CTA', category: 'hero', tags: ['hero', 'cta'] },
    { id: 'pattern_split_hero', name: 'Split Hero', description: 'Two-column hero with text and image', category: 'hero', tags: ['hero', 'image'] },
    { id: 'pattern_feature_grid', name: 'Feature Grid', description: '3-column features with icons', category: 'features', tags: ['features', 'grid'] },
    { id: 'pattern_feature_list', name: 'Feature List', description: 'Vertical feature list with checkmarks', category: 'features', tags: ['features', 'list'] },
    { id: 'pattern_cta_banner', name: 'CTA Banner', description: 'Centered call-to-action banner', category: 'cta', tags: ['cta', 'banner'] },
    { id: 'pattern_content_section', name: 'Content Section', description: 'Two-column text + image', category: 'content', tags: ['content', 'image'] },
    { id: 'pattern_pricing_cards', name: 'Pricing Cards', description: '3-tier pricing table', category: 'e-commerce', tags: ['pricing', 'cards'] },
    { id: 'pattern_testimonial_cards', name: 'Testimonial Cards', description: '3-column testimonials', category: 'testimonials', tags: ['testimonials', 'cards'] },
    { id: 'pattern_simple_header', name: 'Simple Header', description: 'Clean header with logo and nav', category: 'header', tags: ['header', 'nav'] },
    { id: 'pattern_logo_header', name: 'Logo Header', description: 'Header with logo image and CTA', category: 'header', tags: ['header', 'logo'] },
    { id: 'pattern_simple_footer', name: 'Simple Footer', description: 'Minimal centered footer', category: 'footer', tags: ['footer'] },
    { id: 'pattern_multi_column_footer', name: 'Multi-Column Footer', description: 'Full footer with link columns', category: 'footer', tags: ['footer', 'links'] },
    { id: 'pattern_blog_grid', name: 'Blog Grid', description: 'Blog post grid layout', category: 'content', tags: ['blog', 'grid'] },
    { id: 'pattern_product_grid', name: 'Product Grid', description: 'E-commerce product grid', category: 'e-commerce', tags: ['products', 'grid'] },
    { id: 'pattern_contact_form', name: 'Contact Form', description: 'Contact form layout', category: 'forms', tags: ['form', 'contact'] },
  ];

  let filteredPatterns = patterns;

  // Filter by category
  if (params?.category) {
    filteredPatterns = filteredPatterns.filter(p => p.category === params.category);
  }

  // Filter by tags
  if (params?.tags && params.tags.length > 0) {
    filteredPatterns = filteredPatterns.filter(p =>
      params.tags!.some(tag => p.tags.includes(tag))
    );
  }

  // Sort
  if (params?.sortBy === 'name') {
    filteredPatterns.sort((a, b) => a.name.localeCompare(b.name));
  } else if (params?.sortBy === 'category') {
    filteredPatterns.sort((a, b) => a.category.localeCompare(b.category));
  }

  // Paginate
  const offset = params?.offset || 0;
  const limit = params?.limit || 50;
  const paginatedPatterns = filteredPatterns.slice(offset, offset + limit);

  return {
    success: true,
    patterns: paginatedPatterns,
    total: filteredPatterns.length,
    limit,
    offset,
  };
};

export const getPattern = async (patternId: string) => {
  const result = await getPatterns();
  const pattern = result.patterns.find(p => p.id === patternId);

  if (!pattern) {
    throw new APIError(404, `Pattern ${patternId} not found`);
  }

  return {
    success: true,
    pattern,
  };
};

export const insertPattern = async (
  patternId: string,
  pageId: string,
  siteId: string,
  customizations?: Record<string, any>,
  position?: 'start' | 'end'
) => {
  const response = await apiCall(`/api/ai/execute-tool`, {
    method: 'POST',
    body: JSON.stringify({
      toolName: 'insert_pattern',
      args: {
        pageId,
        patternId,
        position: position || 'end',
        customizations,
      },
      context: {
        siteId,
      },
    }),
  });

  return response;
};

export const customizePattern = async (
  pageId: string,
  containerId: string,
  customizations: {
    texts?: Array<{
      path: string;
      content?: string;
      variant?: string;
      color?: string;
      align?: string;
      weight?: string;
    }>;
    buttons?: Array<{
      path: string;
      text?: string;
      variant?: string;
      size?: string;
      href?: string;
    }>;
    images?: Array<{
      path: string;
      src?: string;
      alt?: string;
      aspectRatio?: string;
    }>;
    layout?: {
      direction?: string;
      gap?: string;
      align?: string;
      justify?: string;
    };
    style?: {
      background?: string;
      padding?: string;
      margin?: string;
      borderRadius?: string;
      shadow?: string;
    };
    icons?: Array<{
      path: string;
      name?: string;
      size?: string;
      color?: string;
    }>;
  },
  siteId: string
) => {
  const response = await apiCall(`/api/ai/execute-tool`, {
    method: 'POST',
    body: JSON.stringify({
      toolName: 'customize_pattern',
      args: {
        pageId,
        containerId,
        customizations,
      },
      context: {
        siteId,
      },
    }),
  });

  return response;
};

export const getPatternCategories = async () => {
  return {
    success: true,
    categories: [
      { id: 'hero', name: 'Hero Sections', count: 2 },
      { id: 'features', name: 'Features', count: 2 },
      { id: 'cta', name: 'Call to Action', count: 1 },
      { id: 'content', name: 'Content Sections', count: 2 },
      { id: 'testimonials', name: 'Testimonials', count: 1 },
      { id: 'header', name: 'Headers', count: 2 },
      { id: 'footer', name: 'Footers', count: 2 },
      { id: 'e-commerce', name: 'E-commerce', count: 2 },
      { id: 'forms', name: 'Forms', count: 1 },
    ],
  };
};
